import InjectionSearchDialogHtml from "./InjectionSearchDialogHtml.js";

/**
 * @typedef {Object} InjectionRowState
 * @property {boolean} expanded - the analyte table is shown
 * @property {boolean} loading - the analytes are being fetched
 * @property {boolean} importing - the Import click is being handled
 * @property {string|null} error - why the analytes couldn't be fetched
 * @property {AnalyteRow[]|null} analytes - null until they're fetched
 * @property {Set<string>|null} selectedAnalyteIds - what Import will copy; null until the analytes arrive
 * @property {number|undefined} serverCount - analytes the server reported last time, to notice changes
 * @property {Promise<AnalyteRow[]>|null} request - the fetch in flight, so a 2nd click doesn't fire another
 */

/** A row nobody has touched yet */
const NEVER_TOUCHED = Object.freeze({
    expanded: false, loading: false, importing: false, error: null, analytes: null,
    selectedAnalyteIds: null, serverCount: undefined, request: null
});

/**
 * Lets the user find an injection and import its analytes into whatever they're working on. The
 * analytes of a row can be previewed before importing.
 */
export default class InjectionSearchDialogCmp {
    #html = new InjectionSearchDialogHtml();
    #repository;
    #callbacks = {
        onInjectionSelected: [],
        onCancel: []
    };
    #state = {
        query: '',
        searchError: null,
        isLoadingList: false,
        hasLoaded: false,
        /** @type {Map<string, InjectionRowState>} one record per injection */
        rowsById: new Map(),
        /** @type {InjectionSearchRow[]} */
        recents: [],
        /** @type {InjectionSearchRow[]} */
        injections: []
    };
    #searchTimer = null;
    #isClosed = false;
    #lastReloadId = 0;

    /** @param {InjectionSearchRepository} repository */
    constructor(repository) {
        this.#repository = repository;
    }
    async show() {
        this.#html.show({
            onSearch: this.#onSearch.bind(this),
            onShowAnalytesClicked: this.#onShowAnalytesClicked.bind(this),
            onImportClicked: this.#onImportClicked.bind(this),
            onForgetClicked: this.#onForgetClicked.bind(this),
            onRetryClicked: this.#onRetryClicked.bind(this),
            onAnalyteToggled: this.#onAnalyteToggled.bind(this),
            onAllAnalytesToggled: this.#onAllAnalytesToggled.bind(this),
            onRetrySearchClicked: () => this.#reload(),
            onCancel: () => {
                this.#close();
                this.#callbacks.onCancel.forEach(cb => cb());
            }
        });
        await this.#reload();
    }
    /** @param {function(AnalyteRow[])} callback - gets the analytes the user chose to import */
    onInjectionSelected(callback) {
        this.#callbacks.onInjectionSelected.push(callback);
    }
    /** @param {function} callback */
    onCancel(callback) {
        this.#callbacks.onCancel.push(callback);
    }
    #onSearch(query) {
        this.#state.query = query;
        clearTimeout(this.#searchTimer);
        this.#searchTimer = setTimeout(() => this.#reload(), 250);
    }
    async #onRetryClicked(injectionId) {
        this.#rowOf(injectionId).error = null;
        await this.#showAnalytes(injectionId);
    }
    async #onShowAnalytesClicked(injectionId) {
        const row = this.#rowOf(injectionId);
        row.expanded = !row.expanded;
        if (!row.expanded) {
            this.#draw();
            return;
        }
        await this.#showAnalytes(injectionId);
    }
    async #showAnalytes(injectionId) {
        const analytes = await this.#loadAnalytes(injectionId);
        this.#draw();
        if (!analytes?.length)
            return analytes;
        await this.#repository.loadStructures(analytes);
        this.#draw();
        return analytes;
    }

    #onForgetClicked(injectionId) {
        this.#repository.forgetInjection(injectionId);
        this.#state.rowsById.delete(injectionId);
        this.#state.recents = this.#state.recents.filter(injection => injection.id !== injectionId);
        this.#draw();
    }

    #onAnalyteToggled(injectionId, analyteId, isChecked) {
        const row = this.#state.rowsById.get(injectionId);
        if (!row?.selectedAnalyteIds)
            return;
        if (isChecked)
            row.selectedAnalyteIds.add(analyteId);
        else
            row.selectedAnalyteIds.delete(analyteId);
        this.#html.redrawSelectionOf(injectionId, row);
    }

    #onAllAnalytesToggled(injectionId, isChecked) {
        const row = this.#state.rowsById.get(injectionId);
        if (!row?.analytes)
            return;
        row.selectedAnalyteIds = new Set(isChecked ? row.analytes.map(analyte => analyte.id) : []);
        this.#html.redrawSelectionOf(injectionId, row);
    }

    async #onImportClicked(injectionId) {
        const row = this.#rowOf(injectionId);
        if (row.importing) // double-click while analytes are being fetched
            return;
        const injection = this.#injectionOf(injectionId);
        row.importing = true;
        this.#draw();
        let analytes;
        try {
            analytes = await this.#loadAnalytes(injectionId);
        } finally {
            row.importing = false;
        }
        if (!analytes || !analytes.length) {
            row.expanded = true;
            this.#draw();
            return;
        }
        const selected = row.selectedAnalyteIds;
        const toImport = selected ? analytes.filter(analyte => selected.has(analyte.id)) : analytes;
        if (!toImport.length || this.#isClosed) { // they could've hit Esc while we were fetching
            this.#draw();
            return;
        }
        // analytes are on their way out of it - that's what makes it recent, not how the import ends
        this.#repository.rememberInjection(injection);
        this.#close();
        this.#callbacks.onInjectionSelected.forEach(cb => cb(toImport));
    }

    /**
     * @param {string} injectionId
     * @return {Promise<AnalyteRow[]>}
     */
    async #loadAnalytes(injectionId) {
        const row = this.#rowOf(injectionId);
        if (row.analytes)
            return row.analytes;
        if (row.request)
            return row.request.catch(() => null);
        row.error = null;
        row.loading = true;
        this.#draw();
        row.request = this.#repository.getAnalytes(injectionId);
        try {
            const analytes = await row.request || [];
            row.analytes = analytes;
            row.selectedAnalyteIds = new Set(analytes.map(analyte => analyte.id)); // import all unless told otherwise
            return analytes;
        } catch (e) {
            this.#rethrowIfSessionExpired(e);
            row.error = this.#errorMessage(e, 'Could not load analytes');
            return null;
        } finally {
            row.request = null;
            row.loading = false;
        }
    }

    async #reload() {
        const state = this.#state;
        const reloadId = ++this.#lastReloadId;
        state.isLoadingList = true;
        this.#draw();
        let listings, error = null;
        try {
            listings = await this.#repository.searchInjectionsByName(state.query);
        } catch (e) {
            this.#rethrowIfSessionExpired(e);
            error = this.#errorMessage(e, 'Could not load injections');
        }
        if (reloadId !== this.#lastReloadId) // a newer search has already been fired
            return;
        state.isLoadingList = false;
        state.searchError = error;
        state.hasLoaded = state.hasLoaded || !error;
        if (error) { // the rows we already show are better than an empty dialog with an error on top
            this.#draw();
            return;
        }
        state.recents = listings.recents;
        state.injections = listings.injections;
        const staleIds = this.#dropOutdatedAnalytes(this.#listedInjections());
        this.#draw();
        const toReload = staleIds.filter(id => this.#rowOf(id).expanded);
        if (!toReload.length)
            return;
        await Promise.all(toReload.map(id => this.#showAnalytes(id)));
    }

    /**
     * @param {InjectionSearchRow[]} injections
     * @return {string[]} injections whose analytes we had cached and just threw away
     */
    #dropOutdatedAnalytes(injections) {
        const staleIds = [];
        for (const injection of injections) {
            const row = this.#rowOf(injection.id); // every listed injection has a record, so drawing never invents one
            if (!Number.isInteger(injection.analytesCount))
                continue;
            const previousCount = row.serverCount;
            row.serverCount = injection.analytesCount;
            // compared with what the server said before, not with what we cached: if the server keeps reporting
            // a count our analytes don't add up to, re-fetching them on every search would change nothing
            if (previousCount === undefined || previousCount === injection.analytesCount || !row.analytes)
                continue;
            row.analytes = null;
            row.selectedAnalyteIds = null; // it's other analytes now, so what the user had picked means nothing
            staleIds.push(injection.id);
        }
        return staleIds;
    }

    /**
     * @param {HttpError} e
     * @param {string} fallbackPrefix
     * @return {string}
     */
    #errorMessage(e, fallbackPrefix) {
        if (e?.isForbidden)
            return "You don't have read access to this injection.";
        if (e?.isNotFound)
            return 'This injection no longer exists.';
        return `${fallbackPrefix}: ${e?.message || String(e)}`;
    }

    #rethrowIfSessionExpired(e) {
        if (!e?.isUserSessionExpired)
            return;
        this.#state.isLoadingList = false;
        for (const row of this.#state.rowsById.values()) {
            row.loading = false;
            row.importing = false;
            if (!row.analytes && !row.error) // nothing was loaded, so don't claim "No analytes"
                row.expanded = false;
        }
        this.#draw();
        throw e;
    }

    /**
     * @param {string} injectionId
     * @return {InjectionRowState} the record of that injection, created on the first ask
     */
    #rowOf(injectionId) {
        const rows = this.#state.rowsById;
        let row = rows.get(injectionId);
        if (!row)
            rows.set(injectionId, row = {...NEVER_TOUCHED});
        return row;
    }

    /** @return {InjectionSearchRow} */
    #injectionOf(injectionId) {
        return this.#listedInjections().find(injection => injection.id === injectionId);
    }

    /** @return {InjectionSearchRow[]} everything the list is drawn from, recents first */
    #listedInjections() {
        return [...this.#state.recents, ...this.#state.injections];
    }

    /** Whatever is in flight must not draw into a dialog that isn't on the screen anymore. */
    #close() {
        this.#isClosed = true;
        clearTimeout(this.#searchTimer);
        this.#html.close();
    }

    #draw() {
        if (this.#isClosed)
            return;
        this.#html.render(this.#state);
    }
}
